import { useEffect, useRef } from 'react';

import { AUTH_ACTIVITY_EVENT, AUTH_LOGOUT_REASON, AUTH_TIMEOUT } from '../constants/auth';
import { authActions, selectAuthUser } from '../store/authSlice';
import { useAppDispatch, useAppSelector } from '../store/hooks';

/** Keeps server-side last_activity fresh while an authenticated user is active in the browser. */
const AuthActivityTracker: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectAuthUser);
  const lastHeartbeatAtRef = useRef(0);
  const inactivityTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!user) {
      lastHeartbeatAtRef.current = 0;
      if (inactivityTimerRef.current) window.clearTimeout(inactivityTimerRef.current);
      return undefined;
    }

    /** Resets the local idle timer so inactive browser sessions log out after the configured timeout. */
    const scheduleInactivityLogout = () => {
      if (inactivityTimerRef.current) window.clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = window.setTimeout(() => {
        dispatch(authActions.logoutRequest({ reason: AUTH_LOGOUT_REASON.INACTIVITY, notifyServer: true }));
      }, AUTH_TIMEOUT.INACTIVITY_MS);
    };

    /** Sends a throttled auth heartbeat when real browser activity is detected. */
    const handleActivity = () => {
      scheduleInactivityLogout();

      const now = Date.now();
      if (now - lastHeartbeatAtRef.current < AUTH_TIMEOUT.ACTIVITY_HEARTBEAT_MS) return;

      lastHeartbeatAtRef.current = now;
      dispatch(authActions.refreshServerActivityRequest());
    };

    AUTH_ACTIVITY_EVENT.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });
    scheduleInactivityLogout();

    return () => {
      if (inactivityTimerRef.current) window.clearTimeout(inactivityTimerRef.current);
      AUTH_ACTIVITY_EVENT.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
    };
  }, [dispatch, user]);

  return null;
};

export default AuthActivityTracker;
